import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { EllipsisVerticalIcon } from "@heroicons/react/24/solid";
import { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import Avatar from "@/atoms-react/avatar/Avatar.react";
import { Base } from "@/atoms-react/text/Text.react";
import { role } from "@/features/auth/types";
import { AgentNatureMandate, OlkyPassProLinkType } from "@/features/company/types";
import { MiniShieldIcon } from "@/molecules/mini-shield-icon/mini-shield-icon";
import {
  OlkyPassCustomerStatus,
  OlkyPassCustomerStatusLabel
} from "@/molecules/olkypass-customer-status/label";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { MenuItemWithIcon } from "../../../../../molecules/menu-item-with-icon/menu-item-with-icon";
import { RepresentativeItem } from "..";

type RepresentativeTableItemProps = {
  realIdentityUuid?: string;
  linkType: OlkyPassProLinkType;
  link: RepresentativeItem;
  isLast: boolean;
  enableActions: boolean;
  actionEnabled: boolean;
  // Functions
  openEdit: (data: RepresentativeItem) => void;
  openDelete: (link: RepresentativeItem) => void;
  openDuplicate?: (link: RepresentativeItem) => void;
  openInvite: (representative: RepresentativeItem) => void;
  openInviteCreate: (representative: RepresentativeItem) => void;
};

export const RepresentativeTableItem = (props: RepresentativeTableItemProps) => {
  const { linkType, link, isLast, enableActions, realIdentityUuid } = props;
  const { openDelete, openEdit, openInvite, openInviteCreate, openDuplicate } = props;

  const { t } = useTranslation(["pass"]);

  // Admin can ask multiple times to invite so we access the role
  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = (() => userRoles.includes(role.ADMIN))();

  const relationTitle = `${
    linkType === OlkyPassProLinkType.BENEFICIAL_OWNER
      ? t("business_links.add_link.table_content.beneficial_owner")
      : link.agentNature === AgentNatureMandate.OTHER
        ? (link.agentNatureOther ?? "").replaceAll("_", " ")
        : t(`business_links.add_link.table_content.nature_mandate.${link.agentNature}`)
  }`;

  // We display if needed a text which can be clicked to invite //
  const getStatusText = () => {
    let message: string = "";
    let style: string = "text-sm lg:text-base";
    let mouseClickEvent: MouseEventHandler<HTMLSpanElement> | undefined = undefined;
    if (!props.actionEnabled) return;

    const alreadyAskOrAdmin = link.LeAgentUpgradeOlkyPassAsk === null || isAdmin;

    if (link.auditPending) {
      message = `${t("business_links.add_link.table_content.olkypass_message")} ${
        link.olkypassAuditLvl ?? 0
      }`;
    } else if (link.olkypassLvl === 0 && alreadyAskOrAdmin) {
      mouseClickEvent = () => openInviteCreate(link);
      style = twMerge(style, "text-orange-500 hover:underline cursor-pointer");
      message = t("business_links.add_link.table_content.invite_create_message");
    } else if (
      link.olkypassLvl < link.olkypassRequiredLvl &&
      link.realIdentityUuid !== realIdentityUuid &&
      alreadyAskOrAdmin
    ) {
      mouseClickEvent = () => openInvite(link);
      style = twMerge(style, "text-orange-500 hover:underline cursor-pointer");
      message = t("business_links.add_link.table_content.invite_message");
    } else {
      return;
    }

    return (
      <Base
        dataCy={`company-representative-invite-${link.uuid}`}
        className={style}
        disabled={!props.actionEnabled}
        onClick={mouseClickEvent}
      >
        {message}
      </Base>
    );
  };

  return (
    <div
      key={link.uuid}
      className={twMerge(
        "mt-4 mb-3 flex flex-row justify-between py-1 pt-3 pb-6",
        isLast ? "pb-3" : "border-slate lg:border-b-2 dark:border-slate-700"
      )}
    >
      {enableActions && (
        <div className="flex min-w-[100px] items-center">
          <Menu as="div" className="relative">
            <MenuButton
              data-cy="menu-button"
              disabled={!props.actionEnabled}
              className="rounded-sm border-2 border-slate bg-white/10 p-2 text-sm hover:border-slate-100 hover:bg-white/20 dark:border-slate-700 dark:border-b-2 dark:bg-slate-950"
            >
              <EllipsisVerticalIcon
                data-cy="menu-button"
                aria-hidden="true"
                className={twMerge("size-5 text-primary", !props.actionEnabled && "text-slate-200")}
              />
            </MenuButton>

            <MenuItems className="absolute left-0 z-50 mt-1 whitespace-nowrap rounded-sm border bg-white py-1">
              <MenuItemWithIcon
                title={t("business_links.edit")}
                icon={"Pencil"}
                action={() => openEdit(link)}
                showBorderBottom
              />
              {linkType === OlkyPassProLinkType.AGENT && (
                <MenuItemWithIcon
                  title={t("business_links.duplicate")}
                  icon={"User"}
                  action={() => openDuplicate?.(link)}
                  showBorderBottom
                />
              )}
              <MenuItemWithIcon
                title={t("business_links.delete")}
                icon={"Trash"}
                action={() => openDelete(link)}
              />
            </MenuItems>
          </Menu>
        </div>
      )}

      <div className="flex min-w-[200px] basis-4/12 items-center">
        <Avatar className="mr-6 shrink-0" size={10} fallback={link.name} />
        <Base className="text-sm lg:text-base xl:text-xl">{`${link.name}`}</Base>
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center">
        <Base className="text-slate-600 text-sm lg:text-base xl:text-xl">
          {`${link.birthdate}`}
        </Base>
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center">
        <Base className="text-sm lg:text-base xl:text-xl">{relationTitle}</Base>
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center justify-center">
        <MiniShieldIcon level={link.olkypassRequiredLvl} />
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center justify-center">
        <Base
          className={twMerge(
            link.auditPending && "text-orange-500",
            !link.auditPending && link.olkypassLvl < link.olkypassRequiredLvl && "text-error",
            link.olkypassLvl >= link.olkypassRequiredLvl && "text-success",
            "flex items-center text-sm lg:text-base xl:text-xl"
          )}
        >
          <MiniShieldIcon className="min-w-12" level={link.olkypassLvl} />
          {link.olkypassLvl < link.olkypassRequiredLvl ? (
            <ExclamationCircleIcon
              className="color-red-600 ml-2 h-6 w-6"
              data-tooltip={t("business_links.add_link.table_content.shield_tooltip")}
            />
          ) : (
            <CheckCircleIcon className="ml-2 h-6 w-6 text-success-600" />
          )}
        </Base>
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center justify-center">
        <OlkyPassCustomerStatusLabel
          state={link.olkyPassCustomerStatus ?? OlkyPassCustomerStatus.INACTIVE}
        />
      </div>

      <div className="flex min-w-[100px] basis-2/12 items-center">{getStatusText()}</div>
    </div>
  );
};
